// Generates a branded, shareable square PNG badge for the anti-waste goal.
// Pure client-side Canvas — no external API needed. Supports color variants.

export const BADGE_VARIANTS = [
  { id: "olive", label: "Vert", swatch: "#22C55E" },
  { id: "terracotta", label: "Violet", swatch: "#8B5CF6" },
  { id: "mustard", label: "Ambre", swatch: "#F59E0B" },
  { id: "night", label: "Nuit", swatch: "#0F172A" },
  { id: "gold", label: "Or · Ambassadeur", swatch: "#EAB308", exclusive: true },
];

const THEMES = {
  olive:      { bg: "#F8FAFC", frame: "#E2E8F0", eyebrow: "#16A34A", ink: "#0F172A", muted: "#64748B", disc: "#22C55E", discText: "#FFFFFF", accent: "#F59E0B", accentBg: "rgba(245,158,11,0.16)", pillText: "#0F172A" },
  terracotta: { bg: "#F8FAFC", frame: "#E2E8F0", eyebrow: "#7C3AED", ink: "#0F172A", muted: "#64748B", disc: "#8B5CF6", discText: "#FFFFFF", accent: "#22C55E", accentBg: "rgba(34,197,94,0.16)", pillText: "#0F172A" },
  mustard:    { bg: "#F8FAFC", frame: "#E2E8F0", eyebrow: "#B45309", ink: "#0F172A", muted: "#64748B", disc: "#F59E0B", discText: "#0F172A", accent: "#22C55E", accentBg: "rgba(34,197,94,0.16)", pillText: "#0F172A" },
  night:      { bg: "#0F172A", frame: "rgba(255,255,255,0.14)", eyebrow: "#4ADE80", ink: "#F8FAFC", muted: "#94A3B8", disc: "#22C55E", discText: "#FFFFFF", accent: "#F59E0B", accentBg: "rgba(245,158,11,0.22)", pillText: "#F8FAFC" },
  gold:       { bg: "#FEFCE8", frame: "#FDE68A", eyebrow: "#A16207", ink: "#1C1917", muted: "#78716C", disc: "#EAB308", discText: "#FFFFFF", accent: "#22C55E", accentBg: "rgba(34,197,94,0.16)", pillText: "#1C1917" },
};

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function flame(ctx, x, y, s, color) {
  ctx.save();
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.moveTo(x, y - s);
  ctx.bezierCurveTo(x + s * 0.95, y - s * 0.15, x + s * 0.55, y + s, x, y + s);
  ctx.bezierCurveTo(x - s * 0.55, y + s, x - s * 0.95, y - s * 0.15, x, y - s);
  ctx.fill();
  ctx.restore();
}

export async function generateBadgeBlob(goal, variant = "olive", refUrl = "") {
  const t = THEMES[variant] || THEMES.olive;
  const S = 1080;
  const canvas = document.createElement("canvas");
  canvas.width = S;
  canvas.height = S;
  const ctx = canvas.getContext("2d");

  try {
    await document.fonts.ready;
  } catch (_) {
    /* fonts optional */
  }

  ctx.fillStyle = t.bg;
  ctx.fillRect(0, 0, S, S);

  ctx.strokeStyle = t.frame;
  ctx.lineWidth = 3;
  roundRect(ctx, 46, 46, S - 92, S - 92, 32);
  ctx.stroke();

  ctx.textAlign = "center";

  ctx.fillStyle = t.eyebrow;
  ctx.font = "700 26px 'JetBrains Mono', monospace";
  try { ctx.letterSpacing = "8px"; } catch (_) { /* noop */ }
  ctx.fillText("ANTI-GASPI", S / 2 + 4, 150);
  try { ctx.letterSpacing = "0px"; } catch (_) { /* noop */ }

  ctx.fillStyle = t.ink;
  ctx.font = "800 84px 'Outfit', sans-serif";
  ctx.fillText("FoodFend", S / 2, 250);

  if (variant === "gold") {
    ctx.fillStyle = t.eyebrow;
    ctx.font = "700 24px 'JetBrains Mono', monospace";
    try { ctx.letterSpacing = "6px"; } catch (_) { /* noop */ }
    ctx.fillText("· AMBASSADEUR ·", S / 2 + 3, 300);
    try { ctx.letterSpacing = "0px"; } catch (_) { /* noop */ }
  }

  const cx = S / 2;
  const cy = 570;
  const r = 250;
  ctx.fillStyle = t.disc;
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.fill();

  ctx.fillStyle = t.discText;
  ctx.font = "600 190px 'Outfit', sans-serif";
  ctx.textBaseline = "middle";
  ctx.fillText(`${goal.rate}%`, cx, cy - 10);
  ctx.font = "500 34px 'Plus Jakarta Sans', sans-serif";
  ctx.fillText("sauvés", cx, cy + 130);
  ctx.textBaseline = "alphabetic";

  ctx.fillStyle = t.ink;
  ctx.font = "600 44px 'Plus Jakarta Sans', sans-serif";
  const month = (goal.month_label || "").charAt(0).toUpperCase() + (goal.month_label || "").slice(1);
  ctx.fillText(`Objectif atteint en ${month}`, S / 2, 905);

  if (goal.streak > 0) {
    const label = `Série de ${goal.streak} mois`;
    ctx.font = "700 32px 'JetBrains Mono', monospace";
    const tw = ctx.measureText(label).width;
    const pw = tw + 130;
    const px = (S - pw) / 2;
    const py = 950;
    ctx.fillStyle = t.accentBg;
    roundRect(ctx, px, py, pw, 62, 31);
    ctx.fill();
    flame(ctx, px + 42, py + 31, 20, t.accent);
    ctx.fillStyle = t.pillText;
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";
    ctx.fillText(label, px + 74, py + 33);
    ctx.textAlign = "center";
    ctx.textBaseline = "alphabetic";
  }

  ctx.fillStyle = t.muted;
  const record = goal.best_streak > 1 && goal.streak >= goal.best_streak;
  if (record) {
    ctx.fillStyle = t.eyebrow;
    ctx.font = "700 28px 'Plus Jakarta Sans', sans-serif";
    ctx.fillText("Nouveau record de série !", S / 2, 1032);
  }
  ctx.fillStyle = t.muted;
  if (refUrl) {
    const display = refUrl.replace(/^https?:\/\//, "");
    ctx.font = "700 22px 'JetBrains Mono', monospace";
    ctx.fillText(`Rejoignez-moi → ${display}`, S / 2, record ? 1064 : 1050);
  } else {
    ctx.font = "500 26px 'Plus Jakarta Sans', sans-serif";
    ctx.fillText("Mon anti-gaspi avec FoodFend", S / 2, 1050);
  }

  return new Promise((resolve) => canvas.toBlob((b) => resolve(b), "image/png"));
}

export async function generateImpactBlob(impact, name, refUrl = "") {
  const t = THEMES.olive;
  const S = 1080;
  const canvas = document.createElement("canvas");
  canvas.width = S;
  canvas.height = S;
  const ctx = canvas.getContext("2d");
  try { await document.fonts.ready; } catch (_) { /* fonts optional */ }

  ctx.fillStyle = t.bg;
  ctx.fillRect(0, 0, S, S);
  ctx.strokeStyle = t.frame;
  ctx.lineWidth = 3;
  roundRect(ctx, 46, 46, S - 92, S - 92, 32);
  ctx.stroke();

  ctx.textAlign = "center";
  ctx.fillStyle = t.eyebrow;
  ctx.font = "700 26px 'JetBrains Mono', monospace";
  try { ctx.letterSpacing = "8px"; } catch (_) { /* noop */ }
  ctx.fillText("MON IMPACT ANTI-GASPI", S / 2 + 4, 150);
  try { ctx.letterSpacing = "0px"; } catch (_) { /* noop */ }

  ctx.fillStyle = t.ink;
  ctx.font = "800 78px 'Outfit', sans-serif";
  ctx.fillText(name || "FoodFend", S / 2, 240);

  const stats = [
    { v: `${(impact.money_saved || 0).toLocaleString("fr-FR")} €`, l: "économisés" },
    { v: `${(impact.co2_saved || 0).toLocaleString("fr-FR", { maximumFractionDigits: 1 })} kg`, l: "de CO₂ évités" },
    { v: `${impact.saved || 0}`, l: "produits sauvés" },
  ];
  let y = 400;
  stats.forEach((s, i) => {
    ctx.fillStyle = i === 0 ? t.disc : t.ink;
    ctx.font = "600 96px 'Outfit', sans-serif";
    ctx.fillText(s.v, S / 2, y);
    ctx.fillStyle = t.muted;
    ctx.font = "500 30px 'Plus Jakarta Sans', sans-serif";
    ctx.fillText(s.l, S / 2, y + 44);
    y += 190;
  });

  if (impact.referrals > 0) {
    ctx.fillStyle = t.eyebrow;
    ctx.font = "600 34px 'Plus Jakarta Sans', sans-serif";
    ctx.fillText(`+ ${impact.referrals} ami(s) parrainé(s)`, S / 2, 960);
  }

  ctx.fillStyle = t.ink;
  ctx.font = "800 40px 'Outfit', sans-serif";
  ctx.fillText("FoodFend", S / 2, 1015);
  if (refUrl) {
    ctx.fillStyle = t.muted;
    ctx.font = "700 22px 'JetBrains Mono', monospace";
    ctx.fillText(`Rejoignez-moi → ${refUrl.replace(/^https?:\/\//, "")}`, S / 2, 1052);
  }

  return new Promise((resolve) => canvas.toBlob((b) => resolve(b), "image/png"));
}
